import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from "@material-ui/core/styles";
import { useHistory } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import MultiLangTextField from '../../components/MultiLangTextField';
import { createProphet, getProphets, deleteProphet, reorderProphet } from '../../services/prophetService';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Box, makeStyles, CircularProgress, Snackbar, Tooltip, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import AddIcon from '@material-ui/icons/Add';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import { db } from '../../firebase';
import firebase from 'firebase/app';
import ProphetFormModal from './ProphetFormModal';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  tableContainer: {
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
    background: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
  },
  loadingText: {
    marginTop: 16,
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  actionBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
  },
  deleteBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': {
      backgroundColor: 'rgba(239,68,68,0.1)',
      color: '#ef4444',
    },
  },
  orderBadge: {
    background: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    padding: '4px 8px',
    borderRadius: 6,
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  arabicCell: {
    fontFamily: '"Traditional Arabic", "Scheherazade New", serif',
    fontSize: '1.1rem',
    direction: 'rtl',
  },
  sectionLabel: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    marginBottom: 12,
  },
  countChip: {
    background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.15) 100%)',
    color: '#10b981',
    border: '1px solid rgba(16,185,129,0.3)',
    fontWeight: 700,
    fontSize: '0.85rem',
    height: 36,
    padding: '0 8px',
    borderRadius: 10,
  },
}));

export default function ProphetListPage() {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [syncing, setSyncing] = useState(false);

  const defaultProphets = [
    { arabicName: "آدَم", name: { en: "Adam", bn: "আদম" }, title: { en: "The First Man and Prophet", bn: "প্রথম মানব ও নবী" }, story: { en: "Prophet Adam (AS) was the first human being and the first prophet created by Allah.", bn: "নবী আদম (আঃ) ছিলেন আল্লাহর সৃষ্ট প্রথম মানব এবং প্রথম নবী।" } },
    { arabicName: "إِدْرِيس", name: { en: "Idris", bn: "ইদ্রিস" }, title: { en: "The Prophet of Wisdom", bn: "জ্ঞানের নবী" }, story: { en: "Prophet Idris (AS) is recognized for his immense wisdom and knowledge.", bn: "নবী ইদ্রিস (আঃ) তার অগাধ প্রজ্ঞা ও জ্ঞানের জন্য স্বীকৃত।" } },
    { arabicName: "نُوح", name: { en: "Nuh", bn: "নূহ" }, title: { en: "The Patient Warner", bn: "ধৈর্যশীল সতর্ককারী" }, story: { en: "Prophet Nuh (AS) preached to his people for 950 years.", bn: "নবী নূহ (আঃ) ৯৫০ বছর ধরে তার জাতিকে আহ্বান জানিয়েছিলেন।" } },
    { arabicName: "هُود", name: { en: "Hud", bn: "হুদ" }, title: { en: "Prophet to the People of 'Ad", bn: "আদ জাতির নবী" }, story: { en: "Prophet Hud (AS) was sent to the prosperous but arrogant people of 'Ad.", bn: "নবী হুদ (আঃ)-কে সমৃদ্ধশালী কিন্তু অহংকারী আদ জাতির কাছে পাঠানো হয়েছিল।" } },
    { arabicName: "صَالِح", name: { en: "Salih", bn: "সালেহ" }, title: { en: "Prophet to the People of Thamud", bn: "সামুদ জাতির নবী" }, story: { en: "Prophet Salih (AS) was sent to the people of Thamud.", bn: "নবী সালেহ (আঃ)-কে সামুদ জাতির কাছে পাঠানো হয়েছিল।" } },
    { arabicName: "إِبْرَاهِيم", name: { en: "Ibrahim", bn: "ইব্রাহিম" }, title: { en: "The Friend of Allah (Khalilullah)", bn: "আল্লাহর বন্ধু (খলিলুল্লাহ)" }, story: { en: "Prophet Ibrahim (AS) is a major patriarch in Islam.", bn: "নবী ইব্রাহিম (আঃ) ইসলামের একজন প্রধান পিতৃপুরুষ।" } },
    { arabicName: "لُوط", name: { en: "Lut", bn: "লুত" }, title: { en: "Prophet to Sodom and Gomorrah", bn: "সাদুম ও গোমোরাহ-এর নবী" }, story: { en: "Prophet Lut (AS) was the nephew of Ibrahim (AS).", bn: "নবী লুত (আঃ) ছিলেন ইব্রাহিম (আঃ)-এর ভাতিজা।" } },
    { arabicName: "إِسْمَاعِيل", name: { en: "Isma'il", bn: "ইসমাইল" }, title: { en: "The Forefather of the Arabs", bn: "আরবদের পূর্বপুরুষ" }, story: { en: "Prophet Isma'il (AS) was the firstborn son of Ibrahim (AS).", bn: "নবী ইসমাইল (আঃ) ছিলেন ইব্রাহিম (আঃ)-এর প্রথম সন্তান।" } },
    { arabicName: "إِسْحَاق", name: { en: "Ishaq", bn: "ইসহাক" }, title: { en: "Patriarch of the Israelites", bn: "বনী ইসরাইলের পিতৃপুরুষ" }, story: { en: "Prophet Ishaq (AS) was the second son of Ibrahim (AS).", bn: "নবী ইসহাক (আঃ) ছিলেন ইব্রাহিম (আঃ)-এর দ্বিতীয় পুত্র।" } },
    { arabicName: "يَعْقُوب", name: { en: "Ya'qub", bn: "ইয়াকুব" }, title: { en: "Also known as Israel", bn: "ইসরাইল নামেও পরিচিত" }, story: { en: "Prophet Ya'qub (AS) was the son of Ishaq (AS).", bn: "নবী ইয়াকুব (আঃ) ছিলেন ইসহাক (আঃ)-এর পুত্র।" } },
    { arabicName: "يُوسُف", name: { en: "Yusuf", bn: "ইউসুফ" }, title: { en: "The Prophet of Beauty and Dream Interpretation", bn: "সৌন্দর্য ও স্বপ্ন ব্যাখ্যার নবী" }, story: { en: "Prophet Yusuf (AS) was betrayed by his brothers and sold into slavery in Egypt.", bn: "নবী ইউসুফ (আঃ)-কে তার ভাইয়েরা বিশ্বাসঘাতকতা করে মিশরে দাস হিসেবে বিক্রি করে দেয়।" } },
    { arabicName: "أَيُّوب", name: { en: "Ayyub", bn: "আইয়ুব" }, title: { en: "The Symbol of Patience", bn: "ধৈর্য্যের প্রতীক" }, story: { en: "Prophet Ayyub (AS) was tested by Allah with the loss of his wealth, health, and family.", bn: "নবী আইয়ুব (আঃ)-কে আল্লাহ তার সম্পদ, স্বাস্থ্য ও পরিবার কেড়ে নিয়ে পরীক্ষা করেছিলেন।" } },
    { arabicName: "شُعَيْب", name: { en: "Shu'ayb", bn: "শুয়াইব" }, title: { en: "The Speaker of the Prophets", bn: "নবীদের বক্তা" }, story: { en: "Prophet Shu'ayb (AS) was sent to the people of Midian.", bn: "নবী শুয়াইব (আঃ)-কে মাদিয়ান বাসীদের কাছে পাঠানো হয়েছিল।" } },
    { arabicName: "مُوسَى", name: { en: "Musa", bn: "মুসা" }, title: { en: "The One Who Spoke to Allah (Kalimullah)", bn: "যিনি আল্লাহর সাথে কথা বলেছেন (কালিমুল্লাহ)" }, story: { en: "Prophet Musa (AS) was raised in the palace of Pharaoh.", bn: "নবী মুসা (আঃ) ফেরাউনের প্রাসাদে বড় হয়েছিলেন।" } },
    { arabicName: "هَارُون", name: { en: "Harun", bn: "হারুন" }, title: { en: "The Support of Musa", bn: "মুসার সহযোগী" }, story: { en: "Prophet Harun (AS) was the older brother of Musa (AS).", bn: "নবী হারুন (আঃ) ছিলেন মুসা (আঃ)-এর বড় ভাই।" } },
    { arabicName: "ذُو الْكِفْل", name: { en: "Dhul-Kifl", bn: "যুল-কিফল" }, title: { en: "The Steadfast", bn: "দৃঢ়প্রতিজ্ঞ" }, story: { en: "Prophet Dhul-Kifl (AS) is praised in the Quran as being among the patient and righteous.", bn: "কুরআনে নবী যুল-কিফল (আঃ)-কে একজন ধৈর্যশীল ও নেককার হিসেবে প্রশংসা করা হয়েছে।" } },
    { arabicName: "دَاوُود", name: { en: "Dawud", bn: "দাউদ" }, title: { en: "The King Prophet", bn: "রাজা নবী" }, story: { en: "Prophet Dawud (AS) was a shepherd who defeated the giant Goliath.", bn: "নবী দাউদ (আঃ) ছিলেন একজন রাখাল, যিনি বিশালদেহী জালুতকে পরাজিত করেছিলেন।" } },
    { arabicName: "سُلَيْمَان", name: { en: "Sulayman", bn: "সুলাইমান" }, title: { en: "The King with Mastery over Jinn and Nature", bn: "জিন ও প্রকৃতির উপর কর্তৃত্বশীল রাজা" }, story: { en: "Prophet Sulayman (AS) was the son of Dawud (AS).", bn: "নবী সুলাইমান (আঃ) ছিলেন দাউদ (আঃ)-এর পুত্র।" } },
    { arabicName: "إِلْيَاس", name: { en: "Ilyas", bn: "ইলিয়াস" }, title: { en: "Prophet to the Worshipers of Baal", bn: "বাল মূর্তিপূজারীদের নবী" }, story: { en: "Prophet Ilyas (AS) was sent to the Israelites after they had forsaken the teachings of Musa.", bn: "নবী ইলিয়াস (আঃ)-কে বনী ইসরাইলের কাছে পাঠানো হয়েছিল।" } },
    { arabicName: "الْيَسَع", name: { en: "Al-Yasa'", bn: "আল-ইয়াসা" }, title: { en: "The Successor of Ilyas", bn: "ইলিয়াসের উত্তরসূরি" }, story: { en: "Prophet Al-Yasa' (AS) was the successor to Ilyas (AS).", bn: "নবী আল-ইয়াসা (আঃ) ছিলেন ইলিয়াস (আঃ)-এর উত্তরসূরি।" } },
    { arabicName: "يُونُس", name: { en: "Yunus", bn: "ইউনূস" }, title: { en: "The Companion of the Whale (Dhun-Nun)", bn: "মাছের সঙ্গী (যুন-নুন)" }, story: { en: "Prophet Yunus (AS) left his people in frustration when they rejected him.", bn: "নবী ইউনূস (আঃ) তার জাতির প্রতি হতাশ হয়ে তাদের ছেড়ে চলে যান।" } },
    { arabicName: "زَكَرِيَّا", name: { en: "Zakariyya", bn: "জাকারিয়া" }, title: { en: "The Guardian of Maryam", bn: "মারইয়ামের অভিভাবক" }, story: { en: "Prophet Zakariyya (AS) was a priest in the temple and the guardian of Maryam.", bn: "নবী জাকারিয়া (আঃ) ছিলেন মন্দিরের একজন যাজক এবং মারইয়ামের অভিভাবক।" } },
    { arabicName: "يَحْيَى", name: { en: "Yahya", bn: "ইয়াহইয়া" }, title: { en: "The Chaste Prophet", bn: "নিষ্পাপ নবী" }, story: { en: "Prophet Yahya (AS) was the son of Zakariyya (AS).", bn: "নবী ইয়াহইয়া (আঃ) ছিলেন জাকারিয়া (আঃ)-এর পুত্র।" } },
    { arabicName: "عِيسَى", name: { en: "Isa", bn: "ঈসা" }, title: { en: "The Messiah (Al-Masih)", bn: "মসীহ (আল-মাসীহ)" }, story: { en: "Prophet Isa (AS) was miraculously born to the Virgin Maryam.", bn: "নবী ঈসা (আঃ) অলৌকিকভাবে কুমারী মারইয়ামের গর্ভে জন্মগ্রহণ করেন।" } },
    { arabicName: "مُحَمَّد", name: { en: "Muhammad", bn: "মুহাম্মদ" }, title: { en: "The Seal of the Prophets (Khatam an-Nabiyyin)", bn: "সর্বশেষ নবী (খাতামুন্নাবিয়্যীন)" }, story: { en: "Prophet Muhammad (PBUH) is the final messenger sent to all of humanity.", bn: "নবী মুহাম্মদ (সাঃ) হলেন সমগ্র মানবজাতির জন্য প্রেরিত সর্বশেষ রাসূল।" } },
  ];

  const handleSync = async () => {
    setSyncing(true);
    try {
      const batch = db.batch();
      const col = db.collection('prophet_stories');
      defaultProphets.forEach((prophet, index) => {
        const ref = col.doc();
        batch.set(ref, {
          ...prophet,
          orderIndex: index,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
      });
      await batch.commit();
      setSnackbar({ open: true, message: '✅ 25 prophets synced successfully!' });
      loadData({ page: 1 });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to sync prophets' });
    }
    setSyncing(false);
  };

  const loadData = useCallback(async (opts = {}) => {
    setLoading(true);
    const targetPage = opts.page !== undefined ? opts.page : 1;
    try {
      const result = await getProphets({ page: targetPage });
      setItems(result.items);
      setTotalCount(result.total);
      setTotalPages(result.totalPages);
      setPage(result.page);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load prophets' });
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData({ page: 1 }); }, [loadData]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadData({ page: newPage });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteProphet(deleteTarget.id);
      setSnackbar({ open: true, message: 'Prophet removed successfully' });
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete prophet' });
    }
  };

  const handleReorder = async (item, direction) => {
    const idx = items.indexOf(item);
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= items.length) return;
    const target = items[targetIdx];
    try {
      await Promise.all([
        reorderProphet(item.id, target.orderIndex),
        reorderProphet(target.id, item.orderIndex),
      ]);
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to reorder' });
    }
  };

  const jsonFileInputRef = React.useRef(null);
  const modalFileInputRef = React.useRef(null);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedProphetId, setSelectedProphetId] = useState(null);

  const handleOpenCreateModal = () => {
    setSelectedProphetId(null);
    setFormModalOpen(true);
  };

  const handleOpenEditModal = (id) => {
    setSelectedProphetId(id);
    setFormModalOpen(true);
  };

  const handleJsonUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSyncing(true);
    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);
      const prophetsArray = Array.isArray(jsonData) ? jsonData : (jsonData.prophets || [jsonData]);

      const batch = db.batch();
      const col = db.collection('prophet_stories');
      let count = 0;

      for (let i = 0; i < prophetsArray.length; i++) {
        const prophet = prophetsArray[i];
        const prophetRef = col.doc();
        const { chapters, ...prophetData } = prophet;

        // Normalize family structure
        let spouse = { en: [], bn: [] };
        let sons = { en: [], bn: [] };
        if (prophetData.family) {
          if (prophetData.family.spouse) {
            if (Array.isArray(prophetData.family.spouse)) {
              spouse.en = prophetData.family.spouse;
            } else {
              spouse.en = prophetData.family.spouse.en || [];
              spouse.bn = prophetData.family.spouse.bn || [];
            }
          }
          if (prophetData.family.sons) {
            if (Array.isArray(prophetData.family.sons)) {
              sons.en = prophetData.family.sons;
            } else {
              sons.en = prophetData.family.sons.en || [];
              sons.bn = prophetData.family.sons.bn || [];
            }
          }
        }

        batch.set(prophetRef, {
          arabicName: prophetData.arabicName || '',
          name: prophetData.name || { en: prophetData.englishName || '', bn: prophetData.banglaName || '' },
          description: prophetData.description || { en: prophetData.englishTitle || '', bn: prophetData.banglaTitle || '' },
          imageUrl: prophetData.imageUrl || '',
          title: prophetData.title || { en: '', bn: '', ar: '' },
          era: prophetData.era || { en: '', bn: '', ar: '' },
          nation: prophetData.nation || { en: '', bn: '', ar: '' },
          family: { spouse, sons },
          miracles: prophetData.miracles || [],
          orderIndex: prophetData.orderIndex !== undefined ? prophetData.orderIndex : i,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
        count++;

        if (Array.isArray(chapters) && chapters.length > 0) {
          for (let j = 0; j < chapters.length; j++) {
            const ch = chapters[j];
            const chRef = prophetRef.collection('chapters').doc();
            batch.set(chRef, {
              title: ch.title || { en: ch.englishTitle || '', bn: ch.banglaTitle || '' },
              content: ch.content || { en: ch.englishContent || '', bn: ch.banglaContent || '' },
              orderIndex: ch.orderIndex !== undefined ? ch.orderIndex : j,
              createdAt: firebase.firestore.FieldValue.serverTimestamp(),
              updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
            });
          }
        }
      }

      await batch.commit();
      setSnackbar({ open: true, message: `✅ ${count} prophets imported successfully!` });
      loadData();
    } catch (err) {
      console.error('JSON upload error:', err);
      setSnackbar({ open: true, message: 'Failed to parse or upload JSON file' });
    }
    setSyncing(false);
    if (jsonFileInputRef.current) jsonFileInputRef.current.value = '';
  };

  return (
    <AdminLayout title="Prophet Stories">
      <div className={classes.toolbar}>
        <Typography variant="body2" color="textSecondary">
          Manage prophets and their stories in English and Bangla
        </Typography>
        <Box display="flex" style={{ gap: 12 }} alignItems="center">
          <input type="file" accept=".json" hidden ref={jsonFileInputRef} onChange={handleJsonUpload} />
          <Chip
            label={`Total Prophets: ${totalCount}`}
            className={classes.countChip}
          />

          <Button
            variant="outlined"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={() => jsonFileInputRef.current?.click()}
            disabled={syncing}
          >
            Import JSON
          </Button>

          {!loading && items.length === 0 && !syncing ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<CloudUploadIcon />}
              onClick={handleSync}
              disableElevation
              style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
            >
              Sync Default
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
              onClick={handleOpenCreateModal}
              disableElevation
            >
              {syncing ? 'Syncing...' : 'Add Prophet'}
            </Button>
          )}
        </Box>
      </div>

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} thickness={4} />
          <Typography className={classes.loadingText}>Loading records...</Typography>
        </div>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="60">Order</TableCell>
                <TableCell width="80">Image</TableCell>
                <TableCell width="60">Arabic</TableCell>
                <TableCell width="17%">Name (EN)</TableCell>
                <TableCell width="17%">Name (BN)</TableCell>
                <TableCell width="30%">Description (EN)</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <span className={classes.orderBadge}>{item.orderIndex}</span>
                  </TableCell>
                  <TableCell>
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} />
                    ) : (
                      <Typography variant="body2" style={{ color: theme.palette.text.disabled }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={classes.arabicCell}>{item.arabicName || '-'}</span>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textPrimary" style={{ fontWeight: 500 }}>
                      {item.name?.en || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ color: theme.palette.text.secondary }}>
                      {item.name?.bn || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.85rem' }}>
                      {(item.description?.en || item.title?.en || '').substring(0, 80)}
                      {((item.description?.en || item.title?.en || '').length > 80) ? '...' : ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" justifyContent="flex-end" style={{ gap: 4 }}>
                      <Tooltip title="Manage Chapters">
                        <IconButton className={classes.actionBtn} onClick={() => history.push(`/prophets/${item.id}/chapters`)}>
                          <MenuBookIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Move Up">
                        <IconButton className={classes.actionBtn} onClick={() => handleReorder(item, 'up')}>
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Move Down">
                        <IconButton className={classes.actionBtn} onClick={() => handleReorder(item, 'down')}>
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Prophet Info">
                        <IconButton className={classes.actionBtn} onClick={() => handleOpenEditModal(item.id)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton className={classes.deleteBtn} onClick={() => setDeleteTarget(item)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ padding: "48px 0", color: 'gray' }}>
                    No prophets found. Add the first prophet story.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && totalCount > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={20}
          onPageChange={handlePageChange}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Prophet"
        message="Are you sure you want to permanently remove this prophet and their story?"
        confirmText="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ProphetFormModal
        open={formModalOpen}
        prophetId={selectedProphetId}
        onClose={() => setFormModalOpen(false)}
        onSuccess={() => {
          setFormModalOpen(false);
          loadData();
        }}
      />

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </AdminLayout>
  );
}
